import React from 'react';
import { Check, ChevronRight, Lock } from 'lucide-react';
import { AckSectionKey, Chip, Flow, SectionKey, ackedStatus } from './shared';

// Home of the investor profile: name, overall progress and one card per section.
// Tapping a card opens that section in InvestorProfilePage.

const DONE = ['verified', 'complete', 'done'];

const BANK_LABELS: Record<string, string> = {
    account_holder_name: 'Holder name', account_number: 'Account no.', ifsc_code: 'IFSC', bank_name: 'Bank name', bank_proof: 'passbook',
};

// Progress ring geometry (64px box, 6px stroke).
const R = 27;
const C = 2 * Math.PI * R;

const joinWords = (xs: string[]) => xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

interface Props {
    flow: Flow;
    name: string;
    gated: (k: SectionKey) => boolean;
    onOpen: (k: SectionKey) => void;
    // Wide screens show home beside the open section; that section's card is highlighted.
    activeKey?: SectionKey;
}

const ProfileHome: React.FC<Props> = ({ flow, name, gated, onOpen, activeKey }) => {
    const s = flow.sections;
    const order: SectionKey[] = ['kyc', 'bank', 'nominees', 'ack'];
    const kycAckPending = flow.step === 'bank_nominees' && ['ready', 'sent'].includes(flow.acks?.kyc?.status || '');
    const doneCount = order.filter(k => DONE.includes(s[k])).length;
    const units = flow.units;
    const unitsDone = units.filter(u => !['draft', 'not_started', 'no_nominees'].includes(u.status)).length;
    const digits = String(flow.investor.mobile || '').replace(/\D/g, '').slice(-10);
    const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    const mobile = digits.length === 10 ? `+91 ${digits.slice(0, 5)} ${digits.slice(5)}` : flow.investor.mobile;

    const bankSub = () => {
        if (s.bank === 'missing' || s.bank === 'draft') {
            const miss = flow.bank_missing.map(k => BANK_LABELS[k] || k.replace(/_/g, ' '));
            if (miss.length) return `Please fill ${joinWords(miss)}`;
        }
        return null;
    };
    const SUB: Record<string, string> = {
        complete: 'Saved · tap to edit', draft: 'Not finished yet', verified: 'Verified · tap to view', in_review: 'Our team is checking',
        needs_correction: 'Some fields need your attention', missing: 'Please fill details', no_units: 'Waiting for your units',
        locked: 'Opens after verification', ready: 'Review and confirm', sent: 'Review and confirm', done: 'Confirmed',
    };
    const cards: { key: SectionKey; n: number; label: string; sub: string; chip?: React.ReactNode }[] = [
        { key: 'kyc', n: 1, label: 'KYC details', sub: SUB[s.kyc] || '' },
        { key: 'bank', n: 2, label: 'Bank details', sub: bankSub() || SUB[s.bank] || '' },
        {
            key: 'nominees', n: 3, label: 'Unit nominees',
            sub: units.length && !DONE.includes(s.nominees) && s.nominees !== 'in_review'
                ? `${units.length - unitsDone} of ${units.length} unit${units.length === 1 ? '' : 's'} need nominees`
                : SUB[s.nominees] || '',
            chip: units.length && !DONE.includes(s.nominees) && s.nominees !== 'in_review'
                ? <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-800">{unitsDone}/{units.length}</span>
                : undefined,
        },
        { key: 'ack', n: 4, label: 'Acknowledgment', sub: SUB[s.ack] || '' },
    ];

    return (
        <div className="pb-10 lg:pb-12">
            <section className="bg-[#1B4332] text-white">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 pb-6">
                    <div className="flex items-center gap-4">
                        <span className="w-16 h-16 rounded-full bg-emerald-100 text-[#1B4332] grid place-items-center text-xl font-extrabold shrink-0" aria-hidden="true">{initials}</span>
                        <div className="min-w-0">
                            <div className="text-[28px] sm:text-4xl leading-tight font-extrabold tracking-tight break-words">{name || 'Investor'}</div>
                            {mobile && <div className="mt-0.5 text-base font-semibold text-emerald-100/90 tracking-wide tabular-nums">{mobile}</div>}
                        </div>
                    </div>

                    <div className="mt-6 flex items-center gap-4 p-4 rounded-2xl bg-white/10 ring-1 ring-white/15">
                        <div className="relative w-16 h-16 shrink-0" role="img" aria-label={`${doneCount} of 4 sections done`}>
                            <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
                                <circle cx="32" cy="32" r={R} fill="none" strokeWidth="6" className="stroke-white/20" />
                                <circle cx="32" cy="32" r={R} fill="none" strokeWidth="6" strokeLinecap="round" className="stroke-emerald-300 transition-[stroke-dashoffset] duration-500"
                                    strokeDasharray={C} strokeDashoffset={C * (1 - doneCount / 4)} />
                            </svg>
                            <span className="absolute inset-0 grid place-items-center text-[15px] font-extrabold">{doneCount}/4</span>
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-lg font-extrabold leading-tight">{flow.step === 'complete' ? 'Your profile is complete' : 'Complete your profile'}</h1>
                            <p className="text-[13px] text-emerald-100/90 mt-0.5">{flow.step === 'complete' ? 'All 4 sections done' : `${doneCount} of 4 sections done`}</p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="max-w-2xl mx-auto px-4 sm:px-6 mt-4 space-y-3">

                {cards.map((c, i) => {
                    // Acknowledgment opens once something verified is waiting for (or has had) the investor's confirmation.
                    const ackLater = c.key === 'ack' && s.ack === 'locked';
                    const closed = gated(c.key) || s[c.key] === 'locked' || ackLater;
                    const st = s[c.key];
                    const ok = DONE.includes(st);
                    const chipEl = c.chip || <Chip status={gated(c.key) || ackLater ? 'locked' : c.key === 'ack' ? st : ackedStatus(st, flow.acks?.[c.key as AckSectionKey]?.status)} />;
                    // KYC verified but not acknowledged yet: the action sits inside the KYC card.
                    const ackHere = c.key === 'kyc' && kycAckPending;
                    return (<React.Fragment key={c.key}>
                        <div className={ackHere ? 'rounded-2xl border-2 border-[#1B4332] bg-white' : ''}>
                        <button type="button" onClick={() => onOpen(c.key)} disabled={closed}
                            className={`w-full flex items-center gap-3 p-4 rounded-2xl text-left transition ${ackHere ? 'pb-3' : 'border'} ${closed ? 'bg-slate-50 border-slate-200 cursor-not-allowed' : st === 'needs_correction' ? 'bg-white border-red-300 hover:border-red-400' : activeKey === c.key && !ackHere ? 'bg-[#F4FAF6] border-[#1B4332] ring-1 ring-[#1B4332]' : 'bg-white border-slate-200 hover:border-emerald-300'}`}
                            aria-current={activeKey === c.key ? 'step' : undefined}>
                            <span className={`w-10 h-10 rounded-xl grid place-items-center text-[15px] font-extrabold shrink-0 ${ok ? 'bg-[#1B4332] text-white' : closed ? 'bg-slate-100 text-slate-500' : st === 'needs_correction' ? 'bg-red-100 text-red-800' : 'bg-indigo-50 text-indigo-800'}`}>
                                {ok ? <Check size={18} strokeWidth={3} /> : closed ? <Lock size={16} /> : i + 1}
                            </span>
                            <span className="flex-1 min-w-0">
                                <b className="block text-[15px] leading-snug [overflow-wrap:anywhere]">{c.label}</b>
                                <span className="block text-xs text-slate-500 truncate">{c.key === 'nominees' && !units.length ? 'Place order to unlock this step'
                                    : c.key === 'bank' && !units.length && gated('bank') ? (flow.step === 'kyc' && flow.stage === 'DRAFT' ? 'Complete KYC and place order' : 'Place order to unlock this step')
                                    : ackLater ? 'Opens once our team verifies your details' : gated(c.key) ? 'Opens after you submit your KYC' : c.sub}</span>
                                {/* Narrow phones: the status sits under the text so it never covers the title. */}
                                <span className="sm:hidden block mt-1.5">{chipEl}</span>
                            </span>
                            <span className="max-sm:hidden shrink-0">{chipEl}</span>
                            {!closed && <ChevronRight size={18} className="text-slate-400 shrink-0" />}
                        </button>
                        {ackHere && (
                            <div className="px-4 pb-4">
                                <button type="button" onClick={() => onOpen('kyc')}
                                    className="w-full min-h-[46px] rounded-xl bg-[#1B4332] text-white text-sm font-bold">Complete KYC acknowledgment</button>
                            </div>
                        )}
                        </div>
                        {/* Where the locked steps start: everything after KYC until it's submitted
                            (bank and nominees open together then). */}
                        {c.key === 'kyc' && flow.step === 'kyc' && flow.stage === 'DRAFT' && (
                            <div className="flex items-center gap-2 px-1 text-xs font-semibold text-slate-500" role="note">
                                <span className="h-px flex-1 bg-slate-300" />
                                <span className="text-center">Complete KYC and place order to unlock the next steps</span>
                                <span className="h-px flex-1 bg-slate-300" />
                            </div>
                        )}
                    </React.Fragment>);
                })}

            </div>
        </div>
    );
};

export default ProfileHome;
